import { useCallback, useEffect, useState } from 'react';

const TOKEN_KEY = 'gc_token';
const USER_KEY = 'gc_user';
const OWNER_KEY = 'gc_owner_key';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
}

// Stable anonymous owner/saver key for guests (lists are shareable without an account)
export const getOwnerKey = (): string => {
  let k = localStorage.getItem(OWNER_KEY);
  if (!k) {
    k = crypto.randomUUID();
    localStorage.setItem(OWNER_KEY, k);
  }
  return k;
};

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const sync = () => {
      try {
        const raw = localStorage.getItem(USER_KEY);
        setUser(raw ? JSON.parse(raw) : null);
      } catch {
        setUser(null);
      }
    };
    window.addEventListener('gc:auth', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('gc:auth', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const finish = useCallback((token: string, u: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    window.dispatchEvent(new Event('gc:auth'));
    setUser(u);
  }, []);

  const register = useCallback(
    async (email: string, password: string, name?: string) => {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      finish(data.token, data.user);
    },
    [finish]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      finish(data.token, data.user);
    },
    [finish]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.dispatchEvent(new Event('gc:auth'));
    setUser(null);
  }, []);

  const authHeaders = useCallback((): Record<string, string> => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  return { user, register, login, logout, authHeaders };
};
